import { Overlay } from '@x-guard/xgac-types/xgac';
import { useEffect, useRef, useState } from 'react';
import { useGetIdentity } from 'react-admin';
import { Map } from 'leaflet';
import { getCurrentCustomer } from '../lib/currentCustomer';
import '@x-guard/xgac-leaflet-distortable-image';
import { xgacDataProvider } from '../dataProviders/xgacDataProvider';
import { attachOverlayLayers } from './overlayImage';

export const useOverlay = (map: Map | null, selectedFloors?: number[]) => {

  const currentCustomer = getCurrentCustomer();
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [lastCustomer, setLastCustomer] = useState<string>(currentCustomer?.value || '');
  const identity = useGetIdentity();

  useEffect(() => {

    map?.on('unload', () => {

      map.eachLayer((layer: any) => {

        if (layer.editing) {

          const layers = layer.editing.currentHandle?._layers ?? {};
          Object.values(layers).forEach((innerLayer: any) => innerLayer.remove());
          layer.editing.currentHandle = null;

        }
        layer.remove();

      });

    });

  }, [map]);

  useEffect(() => {

    if (currentCustomer && currentCustomer.value !== lastCustomer) {

      setLastCustomer(currentCustomer.value);

    }

  }, [currentCustomer, identity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    const getOverlays = async () => {

      setIsLoading(true);
      const overlayRequest = await xgacDataProvider.getList('overlays', {
        pagination: { page: 1, perPage: -1 },
        sort: { field: 'name', order: 'ASC' },
        filter: {},
      });

      setOverlays(overlayRequest.data || []);
      setIsLoading(false);

    };
    getOverlays();

  }, [lastCustomer]);

  const layersRef = useRef<ReturnType<typeof attachOverlayLayers> | null>(null);
  const overlaysRef = useRef(overlays);
  const floorsRef = useRef(selectedFloors);
  overlaysRef.current = overlays;
  floorsRef.current = selectedFloors;

  const syncVisibleOverlays = () => {

    const floors = floorsRef.current;
    const visible = floors && floors.length > 0
      ? overlaysRef.current.filter((overlay) => floors.includes(overlay.level))
      : overlaysRef.current;
    layersRef.current?.sync(visible);

  };

  useEffect(() => {

    if (!map) return undefined;

    const layers = attachOverlayLayers(map, {
      className: 'leaflet-overlay-opacity',
      editable: false,
      actions: [],
      exposeFeatureId: true,
    });
    layersRef.current = layers;
    syncVisibleOverlays();

    return () => {

      layers.destroy();
      layersRef.current = null;

    };

  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    syncVisibleOverlays();

  }, [overlays, map, selectedFloors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {

    if (!map) return undefined;

    const onMoveEnd = () => syncVisibleOverlays();
    map.on('moveend', onMoveEnd);
    return () => {

      map.off('moveend', onMoveEnd);

    };

  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  return { overlays, isLoading };

};
